/**
 * src/pages/ConsentForms.jsx — the paperwork tracker.
 *
 * One job: show who has not signed yet and let someone chase them. The status
 * shown is the *effective* status the API computes — a pending form whose link
 * has lapsed reads as expired here, even though the stored row still says
 * pending.
 *
 * The "Copy link" button exists for the case the email never arrived: the
 * artist reads the URL out or messages it directly.
 */
import { useState } from 'react';

import { api } from '../lib/api.js';
import { useApi } from '../lib/useApi.js';
import { formatDateTime } from '../lib/format.js';
import { Empty, ErrorBanner, Loader, PageHeader, Pill } from '../components/ui.jsx';

const TABS = [
  { value: 'pending', label: 'Awaiting signature' },
  { value: 'expired', label: 'Expired' },
  { value: 'signed', label: 'Signed' },
  { value: '', label: 'All' },
];

export default function ConsentForms() {
  const [status, setStatus] = useState('pending');
  const query = `/api/consent${status ? `?status=${status}` : ''}`;
  const { data, error, loading, reload } = useApi(query, [status]);
  const forms = data?.forms ?? [];

  return (
    <>
      <PageHeader
        title="Consent forms"
        subtitle="Clients sign before they arrive, so nobody does paperwork in the chair."
        actions={TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            className={`btn btn-sm${status === tab.value ? ' btn-primary' : ''}`}
            onClick={() => setStatus(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      />

      <div className="card">
        <Loader loading={loading} error={error}>
          {forms.length === 0 ? (
            <Empty>
              {status === 'pending' ? 'Nothing outstanding — everyone has signed.' : 'Nothing here.'}
            </Empty>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Appointment</th>
                    <th>Artist</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {forms.map((f) => (
                    <ConsentRow key={f.id} form={f} onChanged={reload} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Loader>
      </div>
    </>
  );
}

/**
 * One row of the tracker, with its own action state so a failed resend shows
 * against the right client rather than at the top of the page.
 *
 * @param {{ form: object, onChanged: () => void }} props
 * @returns {JSX.Element}
 */
function ConsentRow({ form, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [signUrl, setSignUrl] = useState(null);

  const resend = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/appointments/${form.appointmentId}/consent/resend`);
      onChanged();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  /**
   * Fetches the signing URL and puts it on the clipboard.
   *
   * The URL is not in the list payload — it is only fetched when actually
   * needed, so a screenshot of this page does not hand out live signing links
   * for every client at once.
   */
  const copyLink = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = signUrl
        ? { consent: { signUrl } }
        : await api.get(`/api/appointments/${form.appointmentId}/consent`);
      const url = res.consent?.signUrl;
      if (!url) throw new Error('No signing link available.');
      setSignUrl(url);
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td>
        <strong>{form.clientName}</strong>
        <div className="muted small">{form.clientEmail}</div>
        {error ? <ErrorBanner>{error}</ErrorBanner> : null}
      </td>
      <td className="muted mono">{formatDateTime(form.appointmentStartsAt)}</td>
      <td className="muted">{form.artistName}</td>
      <td>
        <Pill status={form.status} />
        {form.status === 'signed' ? (
          <div className="muted small">
            {form.signerName} · {formatDateTime(form.signedAt)}
          </div>
        ) : null}
      </td>
      <td>
        <div className="row wrap">
          {form.status === 'signed' && form.pdfUrl ? (
            <a className="btn btn-sm" href={form.pdfUrl} target="_blank" rel="noreferrer">
              PDF
            </a>
          ) : (
            <>
              <button type="button" className="btn btn-sm" disabled={busy} onClick={resend}>
                Resend
              </button>
              <button type="button" className="btn btn-sm" disabled={busy} onClick={copyLink}>
                {copied ? 'Copied' : 'Copy link'}
              </button>
            </>
          )}
        </div>
      </td>
    </tr>
  );
}
